import React from 'react';
import { Box, Card, Typography, Skeleton } from '@mui/material';
import type { Repository, Scan } from '../../store/api/githubApi';

interface RepositorySummaryProps {
  repository: Repository;
  scan?: Scan | null;
  isLoading?: boolean;
}

export const RepositorySummary: React.FC<RepositorySummaryProps> = ({
  repository,
  scan,
  isLoading = false,
}) => {
  const healthScore = Math.round(scan?.debt_score ?? repository.latest_debt_score ?? 0);
  const criticalCount = scan?.count_critical ?? repository.latest_count_critical ?? 0;
  const highCount = scan?.count_high ?? repository.latest_count_high ?? 0;
  const totalIssues =
    scan?.issues?.length ??
    ((criticalCount +
      highCount +
      (scan?.count_medium ?? repository.latest_count_medium ?? 0) +
      (scan?.count_low ?? repository.latest_count_low ?? 0)) ||
      0);

  const healthColor =
    healthScore >= 75 ? '#16a34a' : healthScore >= 50 ? '#d97706' : '#dc2626';

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.04)',
        p: { xs: 2, sm: 2.5 },
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(4, 1fr)',
          },
          gap: { xs: 2, sm: 1 },
          alignItems: 'center',
        }}
      >
        {/* Current Health */}
        <Box sx={{ px: { sm: 2 } }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#64748b',
              fontSize: '0.78rem',
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Current Health
          </Typography>
          {isLoading ? (
            <Skeleton
              variant="rounded"
              width={75}
              height={32}
              sx={{ borderRadius: '8px', bgcolor: '#f1f5f9' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: { xs: '1.6rem', sm: '1.85rem' },
                  fontWeight: 800,
                  color: healthColor,
                  lineHeight: 1,
                }}
              >
                {healthScore || '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                / 100
              </Typography>
            </Box>
          )}
        </Box>

        {/* Total Issues */}
        <Box
          sx={{
            px: { sm: 2 },
            borderLeft: { sm: '1px solid #f1f5f9' },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#64748b',
              fontSize: '0.78rem',
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Issues
          </Typography>
          {isLoading ? (
            <Skeleton
              variant="rounded"
              width={85}
              height={32}
              sx={{ borderRadius: '8px', bgcolor: '#f1f5f9' }}
            />
          ) : (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: { xs: '1.6rem', sm: '1.85rem' },
                fontWeight: 800,
                color: '#0f172a',
                lineHeight: 1,
              }}
            >
              {totalIssues.toLocaleString()}
            </Typography>
          )}
        </Box>

        {/* Critical */}
        <Box
          sx={{
            px: { sm: 2 },
            borderLeft: { sm: '1px solid #f1f5f9' },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#64748b',
              fontSize: '0.78rem',
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Critical
          </Typography>
          {isLoading ? (
            <Skeleton
              variant="rounded"
              width={45}
              height={32}
              sx={{ borderRadius: '8px', bgcolor: '#f1f5f9' }}
            />
          ) : (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: { xs: '1.6rem', sm: '1.85rem' },
                fontWeight: 800,
                color: criticalCount > 0 ? '#dc2626' : '#16a34a',
                lineHeight: 1,
              }}
            >
              {criticalCount}
            </Typography>
          )}
        </Box>

        {/* High */}
        <Box
          sx={{
            px: { sm: 2 },
            borderLeft: { sm: '1px solid #f1f5f9' },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#64748b',
              fontSize: '0.78rem',
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            High
          </Typography>
          {isLoading ? (
            <Skeleton
              variant="rounded"
              width={45}
              height={32}
              sx={{ borderRadius: '8px', bgcolor: '#f1f5f9' }}
            />
          ) : (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: { xs: '1.6rem', sm: '1.85rem' },
                fontWeight: 800,
                color: highCount > 0 ? '#ea580c' : '#16a34a',
                lineHeight: 1,
              }}
            >
              {highCount}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};
